import React, { useState, useEffect, useMemo, useRef } from "react";
import debounce from "lodash.debounce";
import {
  Search,
  Filter,
  Users,
  List,
  MapPin,
  X,
  FileText,
  Database,
  Plus,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  setShowPermissionMessage,
  setShowPermissionModal,
} from "@/dispatchers/appDispatchers";
import { openPatientForm } from "@/dispatchers/patientDispatchers";
import { accessTitanversePatientUpsert } from "@/data/constantStrings";
import { checkUserPermissions } from "@/utils/checkUserPermissions";

interface PatientsListSearchProps {
  isDarkMode: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  assigneeFilter: string;
  onAssigneeFilterChange: (value: string) => void;
  assignees: string[];
  consultationStatusFilter: string;
  onConsultationStatusFilterChange: (value: string) => void;
  showDuplicatesOnly: boolean;
  onShowDuplicatesOnlyChange: (value: boolean) => void;
  duplicateCount: number;
  viewMode?: "list" | "map";
  onViewModeChange?: (mode: "list" | "map") => void;
  treatmentPlanFilter?: string;
  onTreatmentPlanFilterChange?: (value: string) => void;
  searchTitanEnabled?: boolean;
  onSearchTitanEnabledChange?: (value: boolean) => void;
  handleSearch: (value: string) => void;
}

export default function PatientsListSearch({
  isDarkMode,
  searchValue,
  onSearchChange,
  assigneeFilter,
  onAssigneeFilterChange,
  assignees,
  consultationStatusFilter,
  onConsultationStatusFilterChange,
  showDuplicatesOnly,
  onShowDuplicatesOnlyChange,
  duplicateCount,
  viewMode = "list",
  onViewModeChange,
  treatmentPlanFilter = "all",
  onTreatmentPlanFilterChange,
  searchTitanEnabled = false,
  onSearchTitanEnabledChange,
  handleSearch,
}: PatientsListSearchProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [localSearchValue, setLocalSearchValue] = useState(searchValue);

  // Sync local state when parent resets the search (e.g. filter clear)
  useEffect(() => {
    setLocalSearchValue(searchValue);
  }, [searchValue]);

  // Use refs so the debounced fn always calls the latest callbacks
  const callbacksRef = useRef({ onSearchChange, handleSearch });
  callbacksRef.current = { onSearchChange, handleSearch };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        callbacksRef.current.onSearchChange(value);
        callbacksRef.current.handleSearch(value);
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Local state for pending filter changes
  const [pendingConsultationStatus, setPendingConsultationStatus] = useState(
    consultationStatusFilter,
  );
  const [pendingAssignee, setPendingAssignee] = useState(assigneeFilter);
  const [pendingTreatmentPlan, setPendingTreatmentPlan] =
    useState(treatmentPlanFilter);
  const [pendingShowDuplicates, setPendingShowDuplicates] =
    useState(showDuplicatesOnly);
  const [pendingSearchTitan, setPendingSearchTitan] =
    useState(searchTitanEnabled);

  const canAddPatient = checkUserPermissions(accessTitanversePatientUpsert);

  // Sync pending state when popover opens
  useEffect(() => {
    if (filterOpen) {
      setPendingConsultationStatus(consultationStatusFilter);
      setPendingAssignee(assigneeFilter);
      setPendingTreatmentPlan(treatmentPlanFilter);
      setPendingShowDuplicates(showDuplicatesOnly);
      setPendingSearchTitan(searchTitanEnabled);
    }
  }, [
    filterOpen,
    consultationStatusFilter,
    assigneeFilter,
    treatmentPlanFilter,
    showDuplicatesOnly,
    searchTitanEnabled,
  ]);

  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (consultationStatusFilter !== "all") count++;
    if (assigneeFilter !== "all") count++;
    if (showDuplicatesOnly) count++;
    if (treatmentPlanFilter !== "all") count++;
    if (searchTitanEnabled) count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  // Apply pending filters
  const handleApplyFilters = () => {
    onSearchChange("");
    onConsultationStatusFilterChange(pendingConsultationStatus);
    onAssigneeFilterChange(pendingAssignee);
    onShowDuplicatesOnlyChange(pendingShowDuplicates);
    if (onTreatmentPlanFilterChange) {
      onTreatmentPlanFilterChange(pendingTreatmentPlan);
    }
    if (onSearchTitanEnabledChange) {
      onSearchTitanEnabledChange(pendingSearchTitan);
    }
    setFilterOpen(false);
  };

  // Reset all filters (both pending and applied)
  const handleResetFilters = () => {
    setPendingConsultationStatus("all");
    setPendingAssignee("all");
    setPendingShowDuplicates(false);
    setPendingTreatmentPlan("all");
    setPendingSearchTitan(false);

    onConsultationStatusFilterChange("all");
    onAssigneeFilterChange("all");
    onShowDuplicatesOnlyChange(false);
    if (onTreatmentPlanFilterChange) {
      onTreatmentPlanFilterChange("all");
    }
    if (onSearchTitanEnabledChange) {
      onSearchTitanEnabledChange(false);
    }

    setFilterOpen(false);
  };

  // Cancel and close popover
  const handleCancel = () => {
    // Reset pending state to current applied state
    setPendingConsultationStatus(consultationStatusFilter);
    setPendingAssignee(assigneeFilter);
    setPendingTreatmentPlan(treatmentPlanFilter);
    setPendingShowDuplicates(showDuplicatesOnly);
    setPendingSearchTitan(searchTitanEnabled);
    setFilterOpen(false);
  };
  return (
    <div className="mb-3">
      <div className="flex items-center gap-4">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
          <Input
            placeholder="Search patients by name, address, or tags..."
            value={localSearchValue}
            maxLength={200}
            onChange={(e) => {
              const value = e.target.value;
              setLocalSearchValue(value);
              debouncedSearch(value);
            }}
            className={`pl-10 transition-colors duration-300 ${
              isDarkMode
                ? "bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                : "bg-white border-gray-300"
            }`}
          />
        </div>

        <Button
          onClick={() => {
            if (!canAddPatient) {
              setShowPermissionMessage("Create or update patient");
              setShowPermissionModal(true);
              return;
            }

            openPatientForm(true);
          }}
          className={`text-sm text-white ${
            canAddPatient
              ? isDarkMode
                ? "bg-gray-600 hover:bg-gray-500"
                : "bg-gray-900 hover:bg-gray-800"
              : isDarkMode
                ? "bg-gray-600 cursor-not-allowed opacity-50"
                : "bg-gray-400 cursor-not-allowed opacity-50"
          }`}
          size="sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Patient
        </Button>

        {/* View Mode Toggle */}
        {onViewModeChange && (
          <div
            className={`flex items-center border rounded-md overflow-hidden ${
              isDarkMode ? "border-gray-700" : "border-gray-300"
            }`}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewModeChange("list")}
              className={`rounded-none px-3 py-2 ${
                viewMode === "list"
                  ? isDarkMode
                    ? "bg-gray-700 text-white"
                    : "bg-gray-200 text-gray-900"
                  : isDarkMode
                    ? "text-gray-400 hover:text-white hover:bg-gray-800"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              <List className="w-4 h-4 mr-2" />
              List
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewModeChange("map")}
              className={`rounded-none px-3 py-2 ${
                viewMode === "map"
                  ? isDarkMode
                    ? "bg-gray-700 text-white"
                    : "bg-gray-200 text-gray-900"
                  : isDarkMode
                    ? "text-gray-400 hover:text-white hover:bg-gray-800"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              <MapPin className="w-4 h-4 mr-2" />
              Map
            </Button>
          </div>
        )}

        {/* Filters Popover Button */}
        <Popover open={filterOpen} onOpenChange={setFilterOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={`relative ${
                isDarkMode
                  ? "bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
                  : "bg-white border-gray-300 hover:bg-gray-50"
              }`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {activeFiltersCount > 0 && (
                <Badge
                  className="ml-2 px-1.5 py-0.5 min-w-[20px] h-5 flex items-center justify-center"
                  variant="default"
                >
                  {activeFiltersCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className={`w-[380px] p-4 ${
              isDarkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h3
                  className={`text-sm font-semibold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Filter Patients
                </h3>
                {activeFiltersCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleResetFilters}
                    className={`h-7 text-xs ${
                      isDarkMode
                        ? "text-gray-400 hover:text-white"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <X className="w-3 h-3 mr-1" />
                    Reset
                  </Button>
                )}
              </div>

              <Separator
                className={isDarkMode ? "bg-gray-700" : "bg-gray-200"}
              />

              {/* Consultation Status Filter */}
              <div className="space-y-2">
                <Label
                  className={`text-xs font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Consultation Status
                </Label>
                <Select
                  value={pendingConsultationStatus}
                  onValueChange={setPendingConsultationStatus}
                >
                  <SelectTrigger
                    className={`w-full ${
                      isDarkMode
                        ? "bg-gray-900 border-gray-700 text-white"
                        : "bg-white border-gray-300"
                    }`}
                  >
                    <SelectValue placeholder="Consultation status" />
                  </SelectTrigger>
                  <SelectContent
                    className={
                      isDarkMode
                        ? "bg-gray-800 border-gray-700"
                        : "bg-white border-gray-300"
                    }
                  >
                    <SelectItem
                      value="all"
                      className={
                        isDarkMode
                          ? "text-white hover:bg-gray-700 focus:bg-gray-700"
                          : "text-gray-900 hover:bg-gray-100 focus:bg-gray-100"
                      }
                    >
                      All Patients
                    </SelectItem>
                    <SelectItem
                      value="active"
                      className={
                        isDarkMode
                          ? "text-white hover:bg-gray-700 focus:bg-gray-700"
                          : "text-gray-900 hover:bg-gray-100 focus:bg-gray-100"
                      }
                    >
                      Active Consultations
                    </SelectItem>
                    <SelectItem
                      value="inactive"
                      className={
                        isDarkMode
                          ? "text-white hover:bg-gray-700 focus:bg-gray-700"
                          : "text-gray-900 hover:bg-gray-100 focus:bg-gray-100"
                      }
                    >
                      No Active Consultation
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Practitioner Filter */}
              <div className="space-y-2">
                <Label
                  className={`text-xs font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Practitioner
                </Label>
                <Select
                  value={pendingAssignee}
                  onValueChange={setPendingAssignee}
                >
                  <SelectTrigger
                    className={`w-full ${
                      isDarkMode
                        ? "bg-gray-900 border-gray-700 text-white"
                        : "bg-white border-gray-300"
                    }`}
                  >
                    <SelectValue placeholder="Filter by practitioner" />
                  </SelectTrigger>
                  <SelectContent
                    className={
                      isDarkMode
                        ? "bg-gray-800 border-gray-700"
                        : "bg-white border-gray-300"
                    }
                  >
                    {assignees.map((assignee) => (
                      <SelectItem
                        key={assignee}
                        value={assignee}
                        className={
                          isDarkMode
                            ? "text-white hover:bg-gray-700 focus:bg-gray-700"
                            : "text-gray-900 hover:bg-gray-100 focus:bg-gray-100"
                        }
                      >
                        {assignee === "all" ? "All Practitioners" : assignee}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Treatment Plan Filter */}
              {/* {onTreatmentPlanFilterChange && (
                <div className="space-y-2">
                  <Label
                    className={`text-xs font-medium flex items-center gap-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Treatment Plan
                  </Label>
                  <Select
                    value={pendingTreatmentPlan}
                    onValueChange={setPendingTreatmentPlan}
                  >
                    <SelectTrigger
                      className={`w-full ${
                        isDarkMode
                          ? "bg-gray-900 border-gray-700 text-white"
                          : "bg-white border-gray-300"
                      }`}
                    >
                      <SelectValue placeholder="Filter by treatment plan" />
                    </SelectTrigger>
                    <SelectContent
                      className={
                        isDarkMode
                          ? "bg-gray-800 border-gray-700"
                          : "bg-white border-gray-300"
                      }
                    >
                      <SelectItem
                        value="all"
                        className={
                          isDarkMode
                            ? "text-white hover:bg-gray-700 focus:bg-gray-700"
                            : "text-gray-900 hover:bg-gray-100 focus:bg-gray-100"
                        }
                      >
                        All Patients
                      </SelectItem>
                      <SelectItem
                        value="with-plan"
                        className={
                          isDarkMode
                            ? "text-white hover:bg-gray-700 focus:bg-gray-700"
                            : "text-gray-900 hover:bg-gray-100 focus:bg-gray-100"
                        }
                      >
                        With Treatment Plan
                      </SelectItem>
                      <SelectItem
                        value="without-plan"
                        className={
                          isDarkMode
                            ? "text-white hover:bg-gray-700 focus:bg-gray-700"
                            : "text-gray-900 hover:bg-gray-100 focus:bg-gray-100"
                        }
                      >
                        Without Treatment Plan
                      </SelectItem>
                      <SelectItem
                        value="active-plan"
                        className={
                          isDarkMode
                            ? "text-white hover:bg-gray-700 focus:bg-gray-700"
                            : "text-gray-900 hover:bg-gray-100 focus:bg-gray-100"
                        }
                      >
                        Active Treatment Plan
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )} */}

              <Separator
                className={isDarkMode ? "bg-gray-700" : "bg-gray-200"}
              />

              {/* Duplicates Toggle */}
              <div
                className={`flex items-center justify-between p-3 rounded-md ${
                  isDarkMode ? "bg-gray-900" : "bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Users
                    className={`w-4 h-4 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  />
                  <Label
                    htmlFor="duplicates-filter-popover"
                    className={`text-sm font-medium cursor-pointer ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Duplicates Only
                  </Label>
                  {duplicateCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="px-2 py-0.5 text-xs"
                    >
                      {duplicateCount}
                    </Badge>
                  )}
                </div>
                <Switch
                  id="duplicates-filter-popover"
                  checked={pendingShowDuplicates}
                  onCheckedChange={setPendingShowDuplicates}
                />
              </div>

              {/* Search Titan Toggle */}
              <div
                className={`flex items-center justify-between p-3 rounded-md ${
                  isDarkMode ? "bg-gray-900" : "bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Database
                    className={`w-4 h-4 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  />
                  <Label
                    htmlFor="search-titan-filter-popover"
                    className={`text-sm font-medium cursor-pointer ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Search Titan
                  </Label>
                </div>
                <Switch
                  id="search-titan-filter-popover"
                  checked={pendingSearchTitan}
                  onCheckedChange={setPendingSearchTitan}
                />
              </div>
              <p
                className={`text-xs -mt-2 px-3 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Automatically include Titan database in all searches
              </p>

              {/* Apply and Cancel Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  className={`flex-1 ${
                    isDarkMode
                      ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                      : ""
                  }`}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleApplyFilters}
                  className={`flex-1 ${
                    isDarkMode
                      ? "bg-black hover:bg-gray-900 text-white"
                      : "bg-black hover:bg-gray-900 text-white"
                  }`}
                >
                  Apply Filters
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
